/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { dateTimeCol, strCol } from '@integec/grid-tools/lib/cols'
import moment from 'moment'
import { connect } from 'react-redux'
import {
  Button,
  DatePicker,
  Empty,
  Icon,
  Input,
  Modal,
  Progress,
  Radio,
  Select,
  Tabs,
  Tooltip,
  message,
  notification,
} from 'antd'
import {
  ExportComponent,
  rowHeaderRender,
  exportDataComponent,
  getApiTargetInfo,
} from 'core'
import {
  Content,
  CriteriaRow,
  Filler,
  FullHeightSizer,
  Title,
} from 'core/screen-util'
import { ErrorContext, SpinContext } from 'context'
import {
  modifiedDateCol,
  modifierCol,
  YNBooleanTypes,
} from 'core/HeaderColTypeProps'
import {
  Grid,
  defaultVirtualizedCellRender,
  toSelectionColProps,
  virtualizedGridRenderer,
} from '@integec/grid-tools'
import {
  cancelSolarPiDataImport,
  getSolarDataIntervals,
  getSolarPiDataImport,
  saveSolarPiDataImport,
} from './solarapi'
import { DATE_TIME_FORMAT_API } from 'enum'
import numeral from 'numeral'
import { getSystemConfigurationInfo } from 'api/pgads'

export const Label = ({ style = {}, label, required }) => (
  <div
    style={{
      display: 'flex',
      alignContent: 'center',
      marginLeft: '10px',
      justifyContent: 'right',
      ...style,
    }}
  >
    {required && <span style={{ color: 'red', fontSize: 15 }}>*</span>}
    {label} :
  </div>
)
const frequencies = ['10 MINUTE', 'HOURLY', 'DAILY']
const colorCodes = {
  COMPLETE: '#37ff33',
  // 'STARTED': '',
  CANCELLED: '#ff8133',
}
const title = 'PI Import Intervals'

const SolarPiDataImport = ({ apiTargetInfo }) => {
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)

  const [activeKey, setActiveKey] = useState('summary')
  const [fuzzyFilter, setFuzzyFilter] = useState(null)
  const [data, setData] = useState([])
  const [intervalData, setIntervalData] = useState([])
  const [importIntervalRange, setImportIntervalRange] = useState(7)
  const [retrieveInterval, setRetrieveInterval] = useState()
  const [selectedDateRangePicker, setSelectedDateRagePicker] = useState([
    moment(),
    moment(),
  ])
  const [selectedRows, setRowsSelected] = useState([])
  const [modalInfo, setModalInfo] = useState()
  const popModal = info => {
    setModalInfo({
      ...info,
      frequencyArr: info?.frequency?.split(',').filter(a => a),
    })
  }
  const [dblClickRow, setDblClickRow] = useState()
  const onChangeCriteria = () => {
    setData([])
    setIntervalData([])
    setRetrieveInterval()
  }

  const dateRangeValidation = ({ start, end }) => {
    const isValid = end.diff(start, 'days') <= parseInt(importIntervalRange)
    if (!isValid) {
      message.error(
        'Selected Date Range should not more than ' +
          importIntervalRange +
          '  days gap.'
      )
    }
    return isValid
  }

  const loadData = () => {
    setSpinning(true)
    getSolarPiDataImport(apiTargetInfo)({
      start: selectedDateRangePicker[0],
      end: selectedDateRangePicker[1],
    }).fork(setError, data => {
      setSpinning(false)
      setData(data)
      setActiveKey('summary')
      setRowsSelected([])
    })
  }

  const loadRunningData = retrieveInterval => {
    setRetrieveInterval(retrieveInterval)
    const start = moment()
      .subtract(retrieveInterval, 'days')
      .startOf('day')
    const end = moment()

    setSpinning(true)
    getSolarPiDataImport(apiTargetInfo)({
      start,
      end,
      byStartTime: true,
    }).fork(setError, data => {
      setSpinning(false)
      setData(data)
      setActiveKey('summary')
      setRowsSelected([])
    })
  }

  useEffect(
    _ => {
      getSystemConfigurationInfo(apiTargetInfo).fork(setError, data => {
        const range = data.find(
          d => d.key === 'SOLAR_PI_IMPORT_INTERVAL_RANGE'
        ) || { value: 7 }
        setImportIntervalRange(range.value)
      })
    },
    [apiTargetInfo, setError]
  )

  const handleDblClick = _ => {
    if (selectedRows.length <= 0) {
      message.error('Please , Select row to Show Intervals')
      return
    }

    setSpinning(true)
    getSolarDataIntervals(apiTargetInfo)({
      transId: selectedRows[0].transId,
    }).fork(setError, data => {
      setSpinning(false)
      setIntervalData(data)
      setActiveKey('details')
    })
  }

  const handleCancel = ({ data, comment: comments }) => {
    const finalData = { ...data, status: 'CANCELLED', comments }

    setSpinning(true)
    cancelSolarPiDataImport(apiTargetInfo)([finalData]).fork(setError, () => {
      loadData()
    })
  }

  const validateData = data => {
    const {
      intervalStart,
      intervalEnd,
      frequencyArr = [],
      intervalStartObj,
      intervalEndObj,
    } = data
    const fieldError = []
    let isError = false
    if (!intervalStart) {
      fieldError.push('Interval Start')
      isError = true
    }
    if (!intervalEnd) {
      fieldError.push('Interval End')
      isError = true
    }
    if (frequencyArr.length === 0) {
      fieldError.push('Frequency')
      isError = true
    }

    if (isError) {
      message.warn('Some of required field are null :' + fieldError.join(','))
      return false
    }

    return dateRangeValidation({
      start: intervalStartObj,
      end: intervalEndObj,
    })
  }

  const onSaveData = (data, show = false) => {
    if (!validateData(data)) return

    const { frequencyArr = [] } = data
    const finalData = {
      ...data,
      frequency: frequencyArr.join(','),
    }

    setSpinning(true)
    saveSolarPiDataImport(apiTargetInfo)([finalData]).fork(setError, () => {
      if (show) setModalInfo({ isNew: true, missingOnly: 'Y' })
      else setModalInfo()
      loadData()
    })
  }

  const cellRenderer = isIntervalRecord => props => {
    const { reactVirtualizedProps: r, gridToolProps: g } = props
    const { rowIndex, style, columnIndex } = r
    const { data, headers } = g
    const rowData = data[rowIndex]

    return defaultVirtualizedCellRender({
      ...props,
      style: {
        ...style,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        // borderTop: rowData.status ? undefined : '1px solid #cc',
        backgroundColor:
          headers[columnIndex].ident === 'status'
            ? colorCodes[rowData.status] || undefined
            : undefined,
      },
      onDoubleClick: isIntervalRecord ? undefined : () => handleDblClick(),
    })
  }

  const SummaryRef = useRef()
  const DetailsRef = useRef()
  const SolarPiDataImportHeaders = [
    strCol({
      ident: 'transId',
      display: 'Transaction ID',
      isFiltered: true,
      sortOrder: 'asc',
      width: 300,
    }),
    dateTimeCol({
      ident: 'intervalStart',
      display: 'Interval Start',
      isFiltered: true,
      sortOrder: 'asc',
      displayFormat: 'MM/DD/YYYY HH',
      width: 150,
    }),
    dateTimeCol({
      ident: 'intervalEnd',
      display: 'Interval End',
      isFiltered: true,
      sortOrder: 'asc',
      displayFormat: 'MM/DD/YYYY HH',
      width: 150,
    }),
    strCol({
      ident: 'frequency',
      display: 'Frequency',
      isFiltered: true,
      sortOrder: 'asc',
      width: 200,
    }),
    strCol({
      ident: 'status',
      display: 'Status',
      isFiltered: true,
      sortOrder: 'asc',
      width: 150,
    }),
    dateTimeCol({
      ident: 'startTime',
      display: 'Start Time',
      isFiltered: true,
      sortOrder: 'asc',
      width: 150,
    }),
    dateTimeCol({
      ident: 'endTime',
      display: 'End Time',
      isFiltered: true,
      sortOrder: 'asc',
      width: 150,
    }),
    strCol({
      ident: 'percentFinished',
      display: '% Complete',
      isFiltered: false,
      width: 150,
      // alignment: 'right',
      dataFormatter: ({ rowData, value }) => {
        const percent = numeral(value.split('%')[0]).format('00.00')
        const statusMap = { COMPLETE: 'success', CANCELLED: 'exception' }
        return (
          <Progress
            style={{ marginLeft: '5px' }}
            percent={percent}
            status={statusMap[rowData.status] || 'active'}
          />
        )
      },
    }),
    strCol({
      ident: 'comments',
      display: 'Comments',
      isFiltered: true,
      width: 400,
    }),
    strCol({
      ident: 'missingOnly',
      display: 'Missing',
      isFiltered: true,
      ...toSelectionColProps(YNBooleanTypes),
    }),
    strCol({
      ident: 'cancelButton',
      display: ' ',
      dataFormatter: ({ rowData }) => (
        <Button
          type="link"
          disabled={['COMPLETE', 'CANCELLED'].includes(rowData.status)}
          style={{ height: '23px', padding: '0px' }}
          onClick={() => setDblClickRow(rowData)}
        >
          Cancel
        </Button>
      ),
    }),
    modifierCol,
    modifiedDateCol,
  ].map(d => ({ ...d, ellipsis: true }))

  return (
    <>
      <div
        style={{
          backgroundColor: '#f0f2f5',
          marginLeft: '20px',
          marginRight: '20px',
        }}
      >
        <CriteriaRow style={{ display: 'flex', width: '100%' }}>
          <Title
            style={{
              marginRight: '15px',
              marginBottom: '5px',
              minWidth: '170px',
            }}
          >
            {title}
          </Title>
          <div>
            <DatePicker.RangePicker
              style={{ marginLeft: '10px' }}
              allowClear={false}
              format="MM/DD/YYYY"
              value={selectedDateRangePicker}
              onChange={d => {
                setSelectedDateRagePicker(d)
                onChangeCriteria()
              }}
            />
          </div>
          <Button
            onClick={() => loadData()}
            type="primary"
            icon="database"
            style={{ marginLeft: '10px' }}
          >
            Retrieve
          </Button>
          <Button
            onClick={() => popModal({ isNew: true, missingOnly: 'Y' })}
            icon="plus-circle"
            style={{ backgroundColor: '#e6f7ff', marginLeft: '5px' }}
          >
            Insert
          </Button>
          <Tooltip title="Retrieve Latest">
            <Radio.Group
              value={retrieveInterval}
              onChange={e => loadRunningData(e.target.value)}
              style={{ marginLeft: '10px' }}
            >
              <Radio.Button value="2">2 Days</Radio.Button>
              <Radio.Button value="5">5 Days</Radio.Button>
              <Radio.Button value="7">Last Week</Radio.Button>
              <Radio.Button value="30">Last Month</Radio.Button>
            </Radio.Group>
          </Tooltip>
          <Filler />
          <Button
            type="link"
            onClick={() => {
              notification.info({
                style: { width: '800px' },
                placement: 'topLeft',
                message: 'PI Import Intervals',
                description: (
                  <span>
                    This screen is used to create intervals to import PI data
                    (by default missing data only).
                    <br />
                    User can insert a new date range with multiple frequency and
                    it will create intervals between the range as below.
                    <br />
                    10 Minute : one interval per one hour, Hourly : one interval
                    for every five hour, Daily : one interval for complete date
                    range.
                    <br />
                    Data pipelines(Auto tasks) created in Power Analytics
                    application will check for these interval and imports data
                    from PI for each interval.
                    <br />
                    User can cancel any transaction and it will stop importing
                    data for that transaction.
                    <br />
                    Double clicking on summary row will open a detail tab where
                    user can see the status of each interval.
                  </span>
                ),
              })
            }}
          >
            <Icon
              type="exclamation-circle"
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
              }}
            />
          </Button>
          <Input.Search
            placeholder="Filter"
            value={fuzzyFilter}
            style={{
              marginLeft: '10px',
              marginRight: '10px',
              flex: '0 0 200px',
            }}
            onChange={e => setFuzzyFilter(e.target.value)}
          />

          <ExportComponent
            getData={_ =>
              exportDataComponent(
                activeKey === 'summary' ? SummaryRef : DetailsRef,
                activeKey === 'summary' ? data : intervalData,
                fuzzyFilter
              )
            }
            data={activeKey === 'summary' ? data : intervalData}
            headers={
              activeKey === 'summary'
                ? SolarPiDataImportHeaders
                : SolarIntervalDataHeaders
            }
            fileName={title}
            btnStyle={{ marginRight: '10px' }}
            disabled={!!(data && data.length <= 0)}
          />
        </CriteriaRow>
        {modalInfo && (
          <CriteriaRow css={{ marginTop: 10 }}>
            <EditModal
              modalInfo={modalInfo}
              setModalInfo={setModalInfo}
              handleSave={onSaveData}
              hide={() => setModalInfo()}
              processing={spinning}
            />
          </CriteriaRow>
        )}
        {dblClickRow && (
          <CancelModal
            data={dblClickRow}
            hide={() => setDblClickRow()}
            onOk={handleCancel}
          />
        )}
        <FullHeightSizer heightOffset={230 + (modalInfo ? 120 : 0)}>
          {({ height, width }) => (
            <Content style={{ height: height - 20 }}>
              <Tabs activeKey={activeKey} onChange={setActiveKey}>
                <Tabs.TabPane key="summary" tab="Summary">
                  <div style={{ height: height - 100, width }}>
                    {data && data.length > 0 ? (
                      <Grid
                        ref={SummaryRef}
                        sortEnabled
                        data={data}
                        headers={SolarPiDataImportHeaders}
                        altBy={u => u.transId}
                        render={virtualizedGridRenderer({
                          rowHeaderRender,
                          height: height - 100,
                          width: width - 60,
                          cellRender: cellRenderer(false),
                          selectionMode: 'single',
                          headerRowHeight: 30,
                        })}
                        fuzzyFilter={fuzzyFilter}
                        isEditable={false}
                        onSelectionChange={rows =>
                          setRowsSelected(rows.selectedRows)
                        }
                        selectionType="row"
                        selectionMode="single"
                        // initialSortOptions={SolarPiDataImportHeaders}
                      />
                    ) : (
                      <Empty
                        imageStyle={{ paddingTop: '30px' }}
                        description={
                          data && data.length <= 0
                            ? 'No Data from Retrieve'
                            : 'Please click on retrieve button to retrieve data'
                        }
                      />
                    )}
                  </div>
                </Tabs.TabPane>
                <Tabs.TabPane key="details" tab="Details">
                  <div style={{ height: height - 100, width }}>
                    {intervalData && intervalData.length > 0 ? (
                      <Grid
                        ref={DetailsRef}
                        sortEnabled
                        data={intervalData}
                        headers={SolarIntervalDataHeaders}
                        render={virtualizedGridRenderer({
                          rowHeaderRender,
                          height: height - 110,
                          width: SolarIntervalDataHeaders.reduce(
                            (a, b) => a + (b.width || 100),
                            0
                          ),
                          headerRowHeight: 30,
                          cellRender: cellRenderer(true),
                        })}
                        fuzzyFilter={fuzzyFilter}
                        isEditable={false}
                        // initialSortOptions={SolarIntervalDataHeaders}
                      />
                    ) : (
                      <Empty
                        imageStyle={{ paddingTop: '30px' }}
                        description="Please double click Summary record for Details"
                      />
                    )}
                  </div>
                </Tabs.TabPane>
              </Tabs>
            </Content>
          )}
        </FullHeightSizer>
      </div>
    </>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
})

export default connect(mapStateToProps)(SolarPiDataImport)

const SolarIntervalDataHeaders = [
  strCol({
    ident: 'transId',
    display: 'Transaction ID',
    isFiltered: true,
    sortOrder: 'asc',
    width: 300,
  }),
  dateTimeCol({
    ident: 'intervalStart',
    display: 'Interval Start',
    isFiltered: true,
    sortOrder: 'asc',
    displayFormat: 'MM/DD/YYYY HH',
    width: 150,
  }),
  dateTimeCol({
    ident: 'intervalEnd',
    display: 'Interval End',
    isFiltered: true,
    sortOrder: 'asc',
    displayFormat: 'MM/DD/YYYY HH',
    width: 150,
  }),
  strCol({
    ident: 'intervalType',
    display: 'Interval Type',
    isFiltered: true,
    sortOrder: 'asc',
    width: 150,
  }),
  strCol({
    ident: 'status',
    display: 'Status',
    isFiltered: true,
    sortOrder: 'asc',
    width: 150,
  }),
  dateTimeCol({
    ident: 'startTime',
    display: 'Start Time',
    isFiltered: true,
    sortOrder: 'asc',
    width: 150,
  }),
  dateTimeCol({
    ident: 'endTime',
    display: 'End Time',
    isFiltered: true,
    sortOrder: 'asc',
    width: 150,
  }),
].map(d => ({ ...d, ellipsis: true }))

const EditModal = ({ handleSave, hide, modalInfo, setModalInfo }) => {
  const { isNew = false } = modalInfo
  const onOk = show => {
    handleSave(modalInfo, show)
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 200px 120px 200px 120px 100px 100px 300px',
        gap: 10,
      }}
    >
      <Label label="Interval Start" required />
      <DatePicker
        allowClear={false}
        value={modalInfo.intervalStartObj}
        onChange={date => {
          const intervalStart = date.set({
            minute: 0,
            second: 0,
            millisecond: 0,
          })
          setModalInfo({
            ...modalInfo,
            intervalStart: intervalStart.format(DATE_TIME_FORMAT_API),
            intervalStartObj: intervalStart,
          })
        }}
        style={{ width: '150px' }}
        showTime={{ format: 'HH', defaultValue: moment('00', 'HH') }}
        format="MM/DD/YYYY HH"
      />
      <Label label="Interval End" required />
      <DatePicker
        allowClear={false}
        value={modalInfo.intervalEndObj}
        onChange={date => {
          const intervalEnd = date.set({
            minute: 0,
            second: 0,
            millisecond: 0,
          })
          setModalInfo({
            ...modalInfo,
            intervalEnd: intervalEnd.format(DATE_TIME_FORMAT_API),
            intervalEndObj: intervalEnd,
          })
        }}
        style={{ width: '150px' }}
        showTime={{ format: 'HH', defaultValue: moment('00', 'HH') }}
        format="MM/DD/YYYY HH"
      />

      <Label label="Missing Only" />
      <Select
        value={modalInfo.missingOnly}
        onChange={c => setModalInfo({ ...modalInfo, missingOnly: c })}
      >
        <Select.Option key="Y" value="Y">
          Yes
        </Select.Option>
        <Select.Option key="N" value="N">
          No
        </Select.Option>
      </Select>
      <Label label="Frequency" required />
      <Select
        mode="multiple"
        value={modalInfo.frequencyArr}
        onChange={c => setModalInfo({ ...modalInfo, frequencyArr: c })}
        maxTagCount={3}
      >
        {frequencies.map(tt => (
          <Select.Option key={tt} value={tt}>
            {tt}
          </Select.Option>
        ))}
      </Select>
      <Label label="Comment" />
      <Input
        style={{ gridColumnEnd: 'span 3' }}
        value={modalInfo.comments}
        onChange={e =>
          setModalInfo({
            ...modalInfo,
            comments: e.target.value,
          })
        }
      />

      <div
        style={{
          display: 'grid',
          gridColumnEnd: 'span 2',
          gridTemplateColumns: 'auto auto auto',
          gap: 10,
        }}
      >
        <Button type="primary" onClick={() => onOk(true)}>
          {isNew ? 'Add' : 'Update'}
        </Button>
        {isNew && (
          <Button type="primary" onClick={() => onOk(false)}>
            Add & Hide
          </Button>
        )}
        <Button type="danger" onClick={_ => hide()}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

const CancelModal = ({ data, onOk, hide }) => {
  const [comment, setComment] = useState('')

  return (
    <Modal
      maskClosable
      title="Cancel Record"
      visible
      onOk={() => {
        onOk({ data, comment })
        hide()
      }}
      onCancel={() => hide()}
      destroyOnClose
      closable
      width={900}
      left={20}
    >
      <div
        style={{
          maxHeight: '600px',
          width: '870px',
          display: 'grid',
          gridTemplateColumns: '1fr',
          overflowX: 'auto',
        }}
      >
        <Input.TextArea
          placeholder="Add Comment"
          value={comment}
          onChange={e => setComment(e.target.value)}
          rows={3}
        />
      </div>
    </Modal>
  )
}
